'use client';

import Link from 'next/link';
import type { Orientation } from '@/types/constants';
import { ORIENTATIONS } from '@/utils/constants';
import type { ExclusivePreLaunchEnhancedContent } from '@/types/zones/exclusive-zone';
import { EXCLUSIVE_LAUNCH_ZONE_DEFAULTS } from '../../constants';
import { CountDownTimerExclusive } from '../countdown';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';

import './styles/@theme';
import { getPathFromLink } from '@/logic/page-config/navigation/paths';
import { classes } from '@/utils/classes/classes';
import { RemindMeButton } from '@/app/components/remind-me-button';
import { REMINDER_MODULES } from '@/tracking/amplitude/events/general/reminder';
import { useShowRemindMe } from '@/hooks/use-show-remind-me';

type ExclusivePreLaunchContentContainerProps = {
    layoutVariant: Orientation;
    content: ExclusivePreLaunchEnhancedContent;
    zoneUid: string;
    gameId: string;
    gameName: string;
};

export const ExclusivePreLaunchContentContainer = ({
    layoutVariant,
    content,
    zoneUid,
    gameId,
    gameName
}: ExclusivePreLaunchContentContainerProps) => {
    const isLandscape = layoutVariant === ORIENTATIONS.LANDSCAPE;
    const { featureFlags } = useGlobalContext();
    const showRemindMe = useShowRemindMe();
    const isEarlyAccess = featureFlags[PRODUCT_THROTTLES.HAS_EARLY_ACCESS];

    const { logo, announcement_background, cta_button_text, launchDate, counter_background_image, category } = content;
    const announcement_text = content.announcement_text || EXCLUSIVE_LAUNCH_ZONE_DEFAULTS.ANNOUNCEMENT_TEXT;

    const logoExclusiveUrl = logo?.exclusive?.url;
    const logoUrl = logo?.url;
    const announcementBackgroundUrl = announcement_background?.url;
    const counterBackgroundImageUrl = counter_background_image?.url;

    const renderLandscapeContent = () => (
        <>
            {logoExclusiveUrl && (
                <img
                    className="exclusive-prelaunch__flex-content__logo-exclusive"
                    alt="Exclusive Game"
                    src={logoExclusiveUrl}
                />
            )}
            {isEarlyAccess ? (
                logoUrl && (
                    <img
                        className="exclusive-prelaunch__flex-content__logo-main"
                        alt="Exclusive Game Logo"
                        src={logoUrl}
                    />
                )
            ) : (
                <>
                    {announcementBackgroundUrl && (
                        <img
                            className="exclusive-prelaunch__flex-content__announcement-image"
                            alt="Announcement Banner"
                            src={announcementBackgroundUrl}
                        />
                    )}
                    {logoUrl && (
                        <img
                            className="exclusive-prelaunch__flex-content__logo-main"
                            alt="Exclusive Game Logo"
                            src={logoUrl}
                        />
                    )}
                </>
            )}
        </>
    );

    const renderCountdownSection = () => {
        if (!launchDate) {
            return null;
        }

        const countdownSectionClass = `exclusive-prelaunch__flex-content__countdown-section--${layoutVariant}${
            isEarlyAccess && isLandscape && ' exclusive-prelaunch__flex-content__countdown-section--early-access'
        }`;

        return (
            <div className={countdownSectionClass}>
                {isLandscape && isEarlyAccess && announcementBackgroundUrl && (
                    <img
                        className="exclusive-prelaunch__flex-content__announcement-image"
                        alt="Announcement Banner"
                        src={announcementBackgroundUrl}
                    />
                )}
                <div className={`exclusive-prelaunch__flex-content__countdown--${layoutVariant}`}>
                    <CountDownTimerExclusive
                        launchDate={launchDate}
                        offset={isLandscape ? 26 : 16}
                        url={counterBackgroundImageUrl}
                        size={isLandscape ? 'large' : 'small'}
                    />
                </div>
            </div>
        );
    };

    const renderPortraitContent = () => {
        if (isEarlyAccess && announcementBackgroundUrl) {
            return (
                <img
                    className="exclusive-prelaunch__flex-content__announcement-image"
                    alt="Announcement Banner"
                    src={announcementBackgroundUrl}
                />
            );
        }
        return (
            <div className="exclusive-prelaunch__flex-content__announcement-text">
                {announcement_text.toUpperCase()}
            </div>
        );
    };

    const renderCtaButton = () => {
        if (!cta_button_text || !category?.uid) {
            return null;
        }

        const categoryPath = getPathFromLink(category);

        return (
            <div
                className={classes(
                    'exclusive-prelaunch__flex-content__navigation',
                    showRemindMe && 'exclusive-prelaunch__flex-content__navigation--with-remind-me'
                )}>
                <Link
                    href={categoryPath || '/'}
                    className={classes(showRemindMe && 'navigate-button--outline')}
                    prefetch={false}>
                    {cta_button_text}
                </Link>
                {showRemindMe && (
                    <RemindMeButton
                        zoneUid={zoneUid}
                        gameId={gameId}
                        module={REMINDER_MODULES.PRE_LAUNCH}
                        gameName={gameName}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={`exclusive-prelaunch__flex-content exclusive-prelaunch__flex-content--${layoutVariant}`}>
            <div
                className={`exclusive-prelaunch__flex-content__wrapper exclusive-prelaunch__flex-content__wrapper--${layoutVariant}`}>
                {isLandscape ? renderLandscapeContent() : renderPortraitContent()}

                {renderCountdownSection()}

                {isLandscape && renderCtaButton()}
            </div>
        </div>
    );
};
